import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronLeft, Plus, Users, Settings, Globe, Lock, Trash2,
    ChevronRight, LayoutDashboard, Search, Calendar, UserCheck, Home, LogOut, FileSpreadsheet
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../../config';
import CollaborationModal from '../../components/CollaborationModal';
import ExcelRoomUploadModal from '../../components/ExcelRoomUploadModal';
import { X, Calendar as CalendarIcon, UserPlus, Shield, UserMinus, AlertCircle, Trash2 as TrashIcon, Info } from 'lucide-react';
import "../Home.css";

const FacultyAdmin = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('owned');
    const [selectedTask, setSelectedTask] = useState(null);
    const [showCollaborationModal, setShowCollaborationModal] = useState(false);
    const [showExcelModal, setShowExcelModal] = useState(false);

    // Creation State
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [newTask, setNewTask] = useState({
        name: "",
        max_members: 6,
        sectionLimits: {},
        genderRequirements: { female: 2 },
        visibility_rules: { departments: [], sections: {}, years: [] },
        expires_at: ""
    });

    // UI Helpers
    const [sectionRuleDept, setSectionRuleDept] = useState("CSE");
    const [sectionRuleCount, setSectionRuleCount] = useState(2);
    const [visRuleDept, setVisRuleDept] = useState("CSE");
    const [visRuleSections, setVisRuleSections] = useState([]);
    const availableDepts = ["AIDS", "BIOTECH", "CIVIL", "CSBS", "CSE", "CSE-AIML&IOT", "CYS", "DS", "ECE", "EEE", "EIE", "IT", "MECH", "R&AI"];
    const availableSections = ['A', 'B', 'C', 'D'];
    const availableYears = ['1st year', '2nd year', '3rd year', '4th year'];

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem("vjconnect_user"));
        if (!userData || (userData.role !== 'faculty' && userData.role !== 'admin')) {
            navigate('/');
            return;
        }
        setUser(userData);
        fetchTasks(userData.email);
    }, [navigate]);

    const fetchTasks = async (email) => {
        try {
            const res = await fetch(`${API_URL}/formation/tasks?email=${email}`);
            const data = await res.json();
            if (data.tasks) setTasks(data.tasks);
        } catch (err) {
            console.error("Failed to fetch tasks:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm("Are you sure you want to delete this room? This action cannot be undone.")) return;
        try {
            const res = await fetch(`${API_URL}/formation/tasks/${taskId}?email=${user.email}`, {
                method: 'DELETE'
            });
            const data = await res.json();
            if (data.success) {
                fetchTasks(user.email);
            } else {
                alert(data.error || "Failed to delete task");
            }
        } catch (err) {
            alert("Error deleting task");
        }
    };

    const handleCreateTask = async () => {
        if (!newTask.name) return alert("Task name is required");
        try {
            const res = await fetch(`${API_URL}/formation/tasks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: newTask.name,
                    creator_email: user.email,
                    max_members: newTask.max_members,
                    rules: JSON.stringify({
                        sectionLimits: newTask.sectionLimits,
                        genderRequirements: newTask.genderRequirements
                    }),
                    visibility_rules: JSON.stringify(newTask.visibility_rules || {}),
                    expires_at: newTask.expires_at || null
                })
            });
            if (res.ok) {
                setShowTaskForm(false);
                fetchTasks(user.email);
                setNewTask({ name: "", max_members: 6, sectionLimits: {}, genderRequirements: { female: 2 }, visibility_rules: { departments: [], sections: {}, years: [] }, expires_at: "" });
            } else {
                alert("Failed to create task");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const addSectionRule = () => {
        setNewTask(prev => ({
            ...prev,
            sectionLimits: { ...(prev.sectionLimits || {}), [sectionRuleDept]: parseInt(sectionRuleCount) }
        }));
    };

    const addVisRule = () => {
        setNewTask(prev => {
            const currentDepts = prev.visibility_rules?.departments || [];
            const newSections = { ...(prev.visibility_rules?.sections || {}) };

            if (visRuleSections.length > 0) {
                newSections[visRuleDept] = Array.from(new Set([...(newSections[visRuleDept] || []), ...visRuleSections])).sort();
            } else {
                delete newSections[visRuleDept];
            }

            const nextDepts = currentDepts.includes(visRuleDept) ? currentDepts : [...currentDepts, visRuleDept].sort();

            return {
                ...prev,
                visibility_rules: {
                    ...(prev.visibility_rules || {}),
                    departments: nextDepts,
                    sections: newSections
                }
            };
        });
        setVisRuleSections([]);
    };

    const toggleLock = async (taskId, currentStatus) => {
        const action = currentStatus === 'locked' ? 'unlock' : 'lock';
        try {
            const res = await fetch(`${API_URL}/formation/tasks/${taskId}/${action}`, { method: 'POST' });
            const data = await res.json();
            if (data.success) fetchTasks(user.email);
        } catch (err) {
            console.error(err);
        }
    };

    const ownedTasks = tasks.filter(t => t.creator_email.toLowerCase() === user?.email.toLowerCase());
    const collabTasks = tasks.filter(t => t.creator_email.toLowerCase() !== user?.email.toLowerCase());

    const displayedTasks = activeTab === 'owned' ? ownedTasks : collabTasks;

    if (loading) return <div className="home-page-container">Loading...</div>;

    return (
        <div className="home-page-container">
            <div className="home-content-wrapper" style={{ padding: '2rem 1rem' }}>
                <header style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                    <button onClick={() => navigate('/faculty/home')} className="back-btn-modern" style={{ position: 'static', transform: 'none', flexShrink: 0 }}>
                        <ChevronLeft size={20} /> Back
                    </button>
                    <div style={{ flex: 1, minWidth: '120px' }}>
                        <h1 className="brand-title" style={{ fontSize: '2rem', margin: 0 }}>Faculty Portal: Formation</h1>
                        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Manage your formation rooms and collaborations</p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        <button onClick={() => navigate('/faculty/home')} title="Home" style={{ width: 46, height: 46, borderRadius: '14px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0 }}><Home size={20} /></button>
                        <button onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }} title="Logout" style={{ width: 46, height: 46, borderRadius: '14px', border: 'none', background: '#ef4444', color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(239,68,68,0.4)', flexShrink: 0 }}><LogOut size={20} /></button>
                    </div>
                </header>

                <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {[
                        { id: 'owned', label: `My Rooms (${ownedTasks.length})` },
                        { id: 'collaborator', label: `Collaborations (${collabTasks.length})` }
                    ].map(t => (
                        <button key={t.id} onClick={() => setActiveTab(t.id)}
                            style={{ padding: '0.8rem 2rem', borderRadius: '30px', border: '1px solid var(--glass-border)', background: activeTab === t.id ? 'var(--accent-color)' : 'var(--glass-bg)', color: activeTab === t.id ? 'white' : 'var(--text-main)', cursor: 'pointer', fontWeight: 'bold', transition: 'all 0.2s' }}
                        >{t.label}</button>
                    ))}
                    {activeTab === 'owned' && (
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem' }}>
                            <button 
                                onClick={() => setShowExcelModal(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.8rem 1.5rem', borderRadius: '30px', border: 'none', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', color: 'white', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 4px 15px rgba(99,102,241,0.3)', transition: 'all 0.2s' }}
                                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'}
                                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <FileSpreadsheet size={18} /> Create via Excel
                            </button>
                            <button 
                                onClick={() => setShowTaskForm(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.8rem 1.5rem', borderRadius: '30px', border: 'none', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 4px 15px rgba(16,185,129,0.3)', transition: 'all 0.2s' }}
                                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'}
                                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <Plus size={18} /> Create Room
                            </button>
                        </div>
                    )}
                </div>

                <div className="grid-responsive" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
                    <AnimatePresence mode="popLayout">
                        {displayedTasks.map((task) => (
                            <motion.div
                                layout
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                key={task.id}
                                className="glass-card"
                                style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.25rem' }}>{task.name}</h3>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                            <Calendar size={14} /> Created: {new Date(task.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div className={`status-badge ${task.status}`}>
                                        {task.status === 'locked' ? <Lock size={12} /> : <Globe size={12} />}
                                        {task.status.toUpperCase()}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                        <Users size={16} style={{ color: 'var(--primary)' }} />
                                        <span>Max: {task.max_members}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                        <UserCheck size={16} style={{ color: 'var(--primary)' }} />
                                        <span>{task.display_creator_name || 'Admin'}</span>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: 'auto pt-1' }}>
                                    <button
                                        className="btn-profile btn-profile-primary"
                                        style={{ width: '100%', padding: '0.6rem' }}
                                        onClick={() => navigate(`/formation/${task.id}`)}
                                    >
                                        <Globe size={16} /> Room View
                                    </button>
                                    <button
                                        className="btn-profile btn-profile-secondary"
                                        style={{ width: '100%', padding: '0.6rem' }}
                                        onClick={() => navigate(`/admin/teams/${task.id}`)}
                                    >
                                        <Users size={16} /> View Teams
                                    </button>

                                    {activeTab === 'owned' && (
                                        <button
                                            className="btn-profile btn-profile-secondary"
                                            style={{ width: '100%', padding: '0.6rem' }}
                                            onClick={() => {
                                                setSelectedTask(task);
                                                setShowCollaborationModal(true);
                                            }}
                                        >
                                            <Users size={16} /> Access
                                        </button>
                                    )}

                                    {activeTab === 'owned' && (
                                        <>
                                            <button
                                                className={`btn-profile ${task.status === 'locked' ? 'btn-profile-secondary' : 'btn-profile-primary'}`}
                                                style={{ width: '100%', padding: '0.6rem' }}
                                                onClick={() => toggleLock(task.id, task.status)}
                                            >
                                                {task.status === 'locked' ? <Globe size={16} /> : <Lock size={16} />}
                                                {task.status === 'locked' ? 'Unlock' : 'Lock'}
                                            </button>
                                            <button
                                                className="btn-profile btn-profile-danger"
                                                style={{ width: '100%', padding: '0.6rem' }}
                                                onClick={() => handleDeleteTask(task.id)}
                                            >
                                                <Trash2 size={16} /> Delete
                                            </button>
                                        </>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {displayedTasks.length === 0 && (
                        <div className="glass-card" style={{ gridColumn: '1 / -1', padding: '3rem', textAlign: 'center', opacity: 0.6 }}>
                            <Info size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                            <p style={{ fontSize: '1.2rem' }}>
                                {activeTab === 'owned' ? "You haven't created any rooms yet." : "You haven't been invited as a collaborator to any rooms yet."}
                            </p>
                        </div>
                    )}

                </div>
            </div>

            {/* Create Task Modal */}
            <AnimatePresence>
                {showTaskForm && (
                    <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="glass-card"
                            style={{ background: '#fff', padding: 30, maxWidth: 500, width: '90%', color: '#333', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}
                        >
                            <button onClick={() => setShowTaskForm(false)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}>
                                <X size={24} />
                            </button>

                            <h3 style={{ marginBottom: 20, fontSize: '1.5rem', fontWeight: '700', color: '#333' }}>Create Formation Task</h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                                <div className="profile-form-row">
                                    <label style={{ display: 'block', marginBottom: 5, fontWeight: '600', color: '#333' }}>Task Name</label>
                                    <input className="profile-input" value={newTask.name} onChange={e => setNewTask({ ...newTask, name: e.target.value })} placeholder="e.g., Hackathon 2024" />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                                    <div className="profile-form-row">
                                        <label style={{ display: 'block', marginBottom: 5, fontWeight: '600', color: '#333' }}>Max Members</label>
                                        <input type="number" className="profile-input" value={newTask.max_members} onChange={e => setNewTask({ ...newTask, max_members: e.target.value })} />
                                    </div>
                                    <div className="profile-form-row">
                                        <label style={{ display: 'block', marginBottom: 5, fontWeight: '600', color: '#333' }}>Expiration Time</label>
                                        <input type="datetime-local" className="profile-input" value={newTask.expires_at || ""} onChange={e => setNewTask({ ...newTask, expires_at: e.target.value })} />
                                    </div>
                                </div>

                                {/* Section Limits (Max students per dept) */}
                                <div className="profile-form-row">
                                    <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold', color: '#333' }}>Section Limits (Max students per dept)</label>
                                    <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                                        <select className="profile-input" style={{ flex: 1 }} value={sectionRuleDept} onChange={e => setSectionRuleDept(e.target.value)}>
                                            {availableDepts.map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                        <input type="number" className="profile-input" style={{ width: 80 }} value={sectionRuleCount} onChange={e => setSectionRuleCount(e.target.value)} />
                                        <button className="btn-profile btn-profile-secondary" onClick={addSectionRule}>Add</button>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                        {Object.entries(newTask.sectionLimits || {}).map(([d, c]) => (
                                            <div key={d} style={{ background: '#f0f9ff', padding: '4px 8px', borderRadius: 4, display: 'flex', gap: 6, alignItems: 'center', border: '1px solid #e0f2fe' }}>
                                                <span style={{ fontSize: '0.85rem', color: '#0369a1' }}>{d}: {c}</span>
                                                <span style={{ cursor: 'pointer', color: 'red', fontWeight: 'bold' }} onClick={() => setNewTask(prev => {
                                                    const next = { ...prev.sectionLimits }; delete next[d]; return { ...prev, sectionLimits: next };
                                                })}>&times;</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Access Control (Visibility Rules) */}
                                <div className="profile-form-row">
                                    <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold', color: '#333' }}>Access Control</label>
                                    <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                                        <select className="profile-input" style={{ flex: 1 }} value={visRuleDept} onChange={e => {
                                            const val = e.target.value;
                                            setVisRuleDept(val);
                                            if (val === "CSE-AIML&IOT") {
                                                setVisRuleSections(prev => prev.filter(x => x !== 'D'));
                                            } else {
                                                setVisRuleSections(prev => prev.filter(x => x !== 'IOT'));
                                            }
                                        }}>
                                            {availableDepts.map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                        <button className="btn-profile btn-profile-secondary" onClick={addVisRule}>Add Dept</button>
                                    </div>

                                    <div style={{ marginBottom: 10 }}>
                                        <label style={{ fontSize: '0.85rem', marginBottom: 5, display: 'block', opacity: 0.7, color: '#333' }}>Sections (Optional):</label>
                                        <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap' }}>
                                            {(visRuleDept === "CSE-AIML&IOT" ? ["A", "B", "C", "IOT"] : availableSections).map(s => (
                                                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.9rem', color: '#333' }}>
                                                    <input type="checkbox" checked={visRuleSections.includes(s)} onChange={e => e.target.checked ? setVisRuleSections([...visRuleSections, s]) : setVisRuleSections(visRuleSections.filter(x => x !== s))} />
                                                    <span>{s}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Years Selection */}
                                    <div style={{ marginTop: 15, padding: 10, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                        <label style={{ fontSize: '0.85rem', marginBottom: 8, display: 'block', fontWeight: 'bold', color: '#333' }}>Target Years:</label>
                                        <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap' }}>
                                            {availableYears.map(year => (
                                                <label key={year} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.9rem', color: '#333' }}>
                                                    <input type="checkbox" checked={newTask.visibility_rules?.years?.includes(year)} onChange={e => {
                                                        const cur = newTask.visibility_rules?.years || [];
                                                        const next = e.target.checked ? [...cur, year] : cur.filter(y => y !== year);
                                                        setNewTask({ ...newTask, visibility_rules: { ...newTask.visibility_rules, years: next } });
                                                    }} />
                                                    <span>{year}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Selected Rules List */}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
                                        {newTask.visibility_rules?.departments?.map(dept => {
                                            const sections = newTask.visibility_rules?.sections?.[dept];
                                            return (
                                                <div key={dept} style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
                                                    <span>{dept} {sections ? `(${sections.join(', ')})` : ''}</span>
                                                    <span style={{ cursor: 'pointer', color: 'red', fontWeight: 'bold' }} onClick={() => setNewTask(prev => {
                                                        const depts = prev.visibility_rules.departments.filter(d => d !== dept);
                                                        const newSecs = { ...prev.visibility_rules.sections }; delete newSecs[dept];
                                                        return { ...prev, visibility_rules: { ...prev.visibility_rules, departments: depts, sections: newSecs } };
                                                    })}>&times;</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Min Female Members */}
                                <div className="profile-form-row">
                                    <label style={{ display: 'block', marginBottom: 5, fontWeight: '600', color: '#333' }}>Min Female Members</label>
                                    <input type="number" className="profile-input" value={newTask.genderRequirements?.female || 0} onChange={e => setNewTask({ ...newTask, genderRequirements: { ...newTask.genderRequirements, female: parseInt(e.target.value) || 0 } })} />
                                </div>

                                <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
                                    <button className="btn-profile btn-profile-secondary" style={{ flex: 1 }} onClick={() => setShowTaskForm(false)}>Cancel</button>
                                    <button className="btn-profile btn-profile-primary" style={{ flex: 1 }} onClick={handleCreateTask}>Create Task</button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {
                showCollaborationModal && selectedTask && (
                    <CollaborationModal
                        taskId={selectedTask.id}
                        taskName={selectedTask.name}
                        requesterEmail={user.email}
                        onClose={() => {
                            setShowCollaborationModal(false);
                            setSelectedTask(null);
                        }}
                    />
                )
            }

            <ExcelRoomUploadModal
                isOpen={showExcelModal}
                onClose={() => setShowExcelModal(false)}
                onSuccess={() => user && fetchTasks(user.email)}
                creatorEmail={user?.email || ''}
            />
        </div >
    );
};

export default FacultyAdmin;
